import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { extractErrorMessage } from '@/utils/format';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  CircularProgress,
  Stack,
  Typography,
  Autocomplete,
} from '@mui/material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { Add as AddIcon, Edit as EditIcon, DeleteOutline as DeleteIcon } from '@mui/icons-material';
import StatusDot from '@/components/common/StatusDot';
import BrandedDialogTitle from '@/components/common/BrandedDialogTitle';
import { TYPE } from '@/theme/typography';
import { settingsApi } from '@/api/endpoints/settings';
import { useNotificationStore } from '@/stores/notificationStore';
import PageHelpIcon from '@/components/PageHelpIcon';
import RecentChangesTimeline from '@/components/RecentChangesTimeline';

/* ── Suggested departments (common across industries) ── */
const SUGGESTED_DEPARTMENTS = [
  { name: 'Administration', code: 'ADMIN' },
  { name: 'Finance & Accounting', code: 'FIN' },
  { name: 'Human Resources', code: 'HR' },
  { name: 'Information Technology', code: 'IT' },
  { name: 'Operations', code: 'OPS' },
  { name: 'Sales & Marketing', code: 'SALES' },
  { name: 'Customer Service', code: 'CS' },
  { name: 'Procurement', code: 'PROC' },
  { name: 'Legal & Compliance', code: 'LEGAL' },
  { name: 'Research & Development', code: 'RND' },
  { name: 'Quality Assurance', code: 'QA' },
  { name: 'Business Development', code: 'BD' },
  { name: 'Logistics & Supply Chain', code: 'LOG' },
  { name: 'Facilities & Maintenance', code: 'FAC' },
  { name: 'Production', code: 'PROD' },
  { name: 'Engineering', code: 'ENG' },
  { name: 'Design', code: 'DSGN' },
  { name: 'Media & Communications', code: 'MEDIA' },
  { name: 'Risk Management', code: 'RISK' },
  { name: 'Internal Audit', code: 'AUDIT' },
  { name: 'Corporate Strategy', code: 'STRAT' },
  { name: 'Health, Safety & Environment', code: 'HSE' },
  { name: 'Training & Development', code: 'TRAIN' },
  { name: 'Property Management', code: 'PROP' },
  { name: 'Construction', code: 'CONST' },
  { name: 'Transport & Fleet', code: 'FLEET' },
];
export default function DepartmentSettingsPage() {
  const queryClient = useQueryClient();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', code: '', description: '' });
  const [codeManuallyEdited, setCodeManuallyEdited] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ name?: string; code?: string }>({});
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const { showSuccess, showError } = useNotificationStore();

  // Auto-suggest a short code from department name
  const suggestCode = (name: string): string => {
    const trimmed = name.trim();
    if (!trimmed) return '';
    const words = trimmed.split(/\s+/).filter(Boolean);
    if (words.length === 1) {
      // Single word: take first 3-4 chars
      return words[0].substring(0, 4).toUpperCase();
    }
    // Multiple words: take first letter of each word (up to 5)
    return words.slice(0, 5).map((w) => w[0]).join('').toUpperCase();
  };

  const handleNameChange = (value: string) => {
    setForm((prev) => ({
      ...prev,
      name: value,
      ...(codeManuallyEdited ? {} : { code: suggestCode(value) }),
    }));
    setFieldErrors((p) => ({ ...p, name: undefined }));
  };

  const handleDescriptionChange = (value: string) =>
    setForm((prev) => ({ ...prev, description: value }));

  const handleCodeChange = (value: string) => {
    setCodeManuallyEdited(true);
    setForm((prev) => ({ ...prev, code: value }));
    setFieldErrors((p) => ({ ...p, code: undefined }));
  };

  const { data: departments, isLoading } = useQuery({
    queryKey: ['settings/reference-data', 'department'],
    queryFn: () => settingsApi.listReferenceData('department'),
    select: (r) => r.data,
  });

  // Filter suggested departments — exclude names/codes already present
  const availableSuggestions = useMemo(() => {
    if (!departments) return SUGGESTED_DEPARTMENTS;
    const existingNames = new Set(departments.map((d: any) => d.name.toLowerCase()));
    const existingCodes = new Set(departments.map((d: any) => d.code.toUpperCase()));
    return SUGGESTED_DEPARTMENTS.filter(
      (s) => !existingNames.has(s.name.toLowerCase()) && !existingCodes.has(s.code),
    );
  }, [departments]);

  const createMutation = useMutation({
    mutationFn: (data: { category: string; code: string; name: string; metadata_?: Record<string, any> }) =>
      settingsApi.createReferenceData(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings/reference-data', 'department'] });
      setDialogOpen(false);
      setForm({ name: '', code: '', description: '' });
      setError('');
      setFieldErrors({});
      showSuccess('Department created successfully');
    },
    onError: (err: any) => {
      setError(extractErrorMessage(err, 'Failed to create department.'));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { code: string; name: string; metadata_?: Record<string, any> } }) =>
      settingsApi.updateReferenceData(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings/reference-data', 'department'] });
      handleCloseDialog();
      showSuccess('Department updated successfully');
    },
    onError: (err: any) => {
      setError(extractErrorMessage(err, 'Failed to update department.'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => settingsApi.deleteReferenceData(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings/reference-data', 'department'] });
      setDeleteTarget(null);
      showSuccess('Department deleted successfully');
    },
    onError: (err: any) => {
      // Backend returns 400 with a clear message when employees are still
      // assigned (BUG-064). Surface it and keep the dialog open.
      showError(extractErrorMessage(err, 'Failed to delete department.'));
    },
  });

  const handleEdit = (row: any) => {
    setEditingId(row.id);
    setForm({ name: row.name, code: row.code, description: row.metadata_?.description ?? '' });
    setCodeManuallyEdited(true); // Don't overwrite existing code when editing
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
    setForm({ name: '', code: '', description: '' });
    setCodeManuallyEdited(false);
    setError('');
    setFieldErrors({});
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const errs: { name?: string; code?: string } = {};
    if (!form.name.trim()) errs.name = 'Department name is required.';
    if (!form.code.trim()) errs.code = 'Code is required.';
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) return;
    // BUG-045: departments are stored in reference_data (no dedicated column),
    // so the free-text description rides in metadata_.
    const metadata_ = { description: form.description.trim() };
    if (editingId) {
      updateMutation.mutate({
        id: editingId,
        data: { code: form.code.trim().toUpperCase(), name: form.name.trim(), metadata_ },
      });
    } else {
      createMutation.mutate({
        category: 'department',
        code: form.code.trim().toUpperCase(),
        name: form.name.trim(),
        metadata_,
      });
    }
  };

  const columns: GridColDef[] = [
    { field: 'name', headerName: 'Department Name', flex: 1, minWidth: 150 },
    { field: 'code', headerName: 'Code', flex: 0.5, minWidth: 80 },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      minWidth: 160,
      valueGetter: (_value, row) => row.metadata_?.description ?? '',
    },
    {
      field: 'is_active',
      headerName: 'Status',
      flex: 0.4,
      minWidth: 70,
      renderCell: (params) => <StatusDot active={params.value} />,
    },
    {
      field: 'is_system',
      headerName: 'System',
      flex: 0.4,
      minWidth: 70,
      renderCell: (params) =>
        params.value ? <Chip label="System" size="small" variant="outlined" /> : null,
    },
    {
      field: 'actions',
      type: 'actions' as const,
      headerName: '',
      width: 50,
      maxWidth: 50,
      getActions: (params: any) => [
        <GridActionsCellItem
          key="edit"
          icon={<EditIcon fontSize="small" />}
          label="Edit"
          showInMenu
          onClick={() => handleEdit(params.row)}
          disabled={params.row.is_system}
        />,
        <GridActionsCellItem
          key="delete"
          icon={<DeleteIcon fontSize="small" />}
          label="Delete"
          showInMenu
          onClick={() => setDeleteTarget(params.row)}
          disabled={params.row.is_system}
        />,
      ],
    },
  ];

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography variant="h5" fontWeight={TYPE.semibold}>Departments</Typography>
          <PageHelpIcon pageKey="settings.department" />
        </Stack>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
          Add Department
        </Button>
      </Stack>

      <Box>
          <DataGrid
            autoHeight
            density="compact"
            rows={departments || []}
            columns={columns}
            disableRowSelectionOnClick
            pageSizeOptions={[10, 25]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
              sorting: { sortModel: [{ field: 'name', sort: 'asc' }] },
            }}
            sx={{ border: 'none' }}
            localeText={{ noRowsLabel: 'No departments found. Click "Add Department" to create one.' }}
          />
      </Box>

      {/* Create Department Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <BrandedDialogTitle>{editingId ? 'Edit Department' : 'Add Department'}</BrandedDialogTitle>
        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}
        >
          {error && <Alert severity="error">{error}</Alert>}
          {editingId ? (
            <TextField
              label="Department Name"
              value={form.name}
              onChange={(e) => handleNameChange(e.target.value)}
              required
              placeholder="e.g. Human Resources"
              error={!!fieldErrors.name}
              helperText={fieldErrors.name}
            />
          ) : (
            <Autocomplete
              freeSolo
              options={availableSuggestions}
              getOptionLabel={(option) =>
                typeof option === 'string' ? option : option.name
              }
              inputValue={form.name}
              onInputChange={(_e, value) => handleNameChange(value)}
              onChange={(_e, value) => {
                if (value && typeof value !== 'string') {
                  // User selected a suggestion — set both name and code
                  setForm((prev) => ({ ...prev, name: value.name, code: value.code }));
                  setCodeManuallyEdited(true); // Lock code to suggestion
                  setFieldErrors({});
                }
              }}
              renderOption={(props, option) => (
                <li {...props} key={option.code}>
                  <Stack direction="row" justifyContent="space-between" width="100%">
                    <Typography variant="body2">{option.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{option.code}</Typography>
                  </Stack>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Department Name"
                  required
                  placeholder="Type or pick from suggestions"
                  error={!!fieldErrors.name}
                  helperText={fieldErrors.name || `${availableSuggestions.length} suggestions available`}
                />
              )}
            />
          )}
          <TextField
            label="Code"
            value={form.code}
            onChange={(e) => handleCodeChange(e.target.value)}
            required
            placeholder="e.g. HR"
            error={!!fieldErrors.code}
            helperText={fieldErrors.code || 'Auto-suggested from name, editable'}
          />
          <TextField
            label="Description"
            value={form.description}
            onChange={(e) => handleDescriptionChange(e.target.value)}
            placeholder="What this department does (optional)"
            multiline
            minRows={2}
            inputProps={{ maxLength: 255 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            {(createMutation.isPending || updateMutation.isPending) ? 'Saving...' : (editingId ? 'Save Changes' : 'Add Department')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Department Confirmation */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => !deleteMutation.isPending && setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <BrandedDialogTitle>Delete Department</BrandedDialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <Typography variant="body2">
            Delete <strong>{deleteTarget?.name}</strong>? This cannot be undone.
            Departments with employees assigned cannot be deleted — reassign
            those employees first.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Recent Changes ── */}
      <RecentChangesTimeline tableName="reference_data" limit={5} />
    </Box>
  );
}
